import {
	Component,
	forwardRef,
	Input,
	OnChanges,
	SimpleChanges
} from '@angular/core';
import { AbstractControl, ValidationErrors, NG_VALIDATORS } from '@angular/forms';
import moment from 'moment';

import { InsightSearchHelper } from 'rev-portal/search/InsightSearchHelper.Service';

import { DialogService } from 'rev-shared/ui/dialog/Dialog.Service';
import { HourMs, MinuteMs } from 'rev-shared/date/Time.Constant';
import { ICategorySettings } from 'rev-shared/ui/categorySettings/ICategorySettings';
import { IMediaFeatures } from 'rev-shared/media/IMediaFeatures';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { TimezoneCodes } from 'rev-shared/date/TimezoneCodes';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { toggleError } from 'rev-shared/util/directives/form/validation/validationUtil';

import { EditWebcastBaseSettingsComponent } from './EditWebcastBaseSettings.Component';
import { EditWebcastModel } from './EditWebcastModel';
import { ShortcutService, IWebcastInfo } from '../webcast/Shortcut.Service';
import { WebcastStatus } from '../webcast/WebcastStatus';

import styles from './EditWebcast.module.less';

export const ShortcutConflictErrorKey = 'shortcutNameInUse';
const MaxPreProductionTimeHours = 72;

@Component({
	selector: 'edit-webcast-basic-settings',
	templateUrl: './EditWebcastBasicSettings.Component.html',
	providers: [{
		provide: NG_VALIDATORS,
		useExisting: forwardRef(() => EditWebcastBasicSettingsComponent),
		multi: true,
	}],
	standalone: false
})
export class EditWebcastBasicSettingsComponent extends EditWebcastBaseSettingsComponent implements OnChanges {
	@Input() public webcast: EditWebcastModel;
	@Input() public features: IMediaFeatures;
	@Input() public isSidebar: boolean;
	@Input() public categories: ICategorySettings[];
	@Input() public externalErrors: ValidationErrors;

	public readonly lobbyTimeDefault: number = 15 * MinuteMs;
	public readonly maxLobbyDurationTimeMs = 2 * HourMs;
	public readonly TimezoneCodes = TimezoneCodes;
	public readonly accountId: string = this.UserContext.getAccount().id;
	public readonly styles = styles;
	public readonly MaxPreProductionTimeHours = MaxPreProductionTimeHours;
	public readonly MaxPreProductionTimeMs = MaxPreProductionTimeHours * HourMs;
	public readonly canEditEvents = this.SecurityContext.checkAuthorization('events.edit');

	public duration: number;
	public exampleDate = moment().startOf('day').hours(14).toDate();
	public preProdAttendeesInsightOptions: any;
	public preProductionEnabled: boolean;
	public shortcutEvents: IWebcastInfo[];

	constructor(
		private readonly UserContext: UserContextService,
		private readonly InsightSearchHelper: InsightSearchHelper,
		private readonly Dialog: DialogService,
		private readonly ShortcutService: ShortcutService,
		private readonly SecurityContext: SecurityContextService
	) {
		super();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if((changes.webcast || changes.features) && this.webcast && this.features) {
			this.onWebcastLoaded();
		}

		if(changes.externalErrors && !changes.externalErrors.isFirstChange()) {
			this.validateExternalErrors();
		}
	}

	public onWebcastLoaded(): void {
		this.updateDuration();

		this.preProductionEnabled = !!this.webcast.preProduction;
		if(this.preProductionEnabled) {
			this.assignPreProdInsightOption();
		}
		this.webcast.timezoneId = this.webcast.timezoneId || undefined;
	}

	public onPreProductionChanged(): void {
		if(!this.preProductionEnabled) {
			this.webcast.preProduction = undefined;
			this.preProdAttendeesInsightOptions = undefined;
			return;
		}

		this.webcast.preProduction = {
			durationMs: undefined,
			attendees: [],
			userIds: [],
			groupIds: []
		};
		this.assignPreProdInsightOption();
		window.setTimeout(() => {
			this.webcast.preProduction.durationMs = this.webcast.lobbyTimeMinutes * MinuteMs;
			const control = this.getControl('preProdDuration');
			control.markAsDirty();
			control.updateValueAndValidity({ onlySelf: true }); //cross check if it is working.
		});
	}

	private assignPreProdInsightOption(): void {
		this.preProdAttendeesInsightOptions = this.InsightSearchHelper.buildAccessEntityInsightOptions({
			accountId: this.accountId,
			users: true,
			groups: true,
			getEntityIds: () => [...this.webcast.preProduction.userIds, ...this.webcast.preProduction.groupIds]
		});
	}

	private updateDuration() {
		if (this.webcast.startDate instanceof Date && this.webcast.endDate instanceof Date) {
			this.duration = this.webcast.endDate.getTime() - this.webcast.startDate.getTime();
		}
	}

	public validateShortcutSchedule(): void {
		window.setTimeout(() => {
			const control = this.getControl('shortcutName');
			control.markAsDirty();
			control.updateValueAndValidity({ onlySelf: true }); //cross check if it is working.
		});
	}

	public shortcutValidator: (value: string) => Promise<ValidationErrors | null> = (value: string) => {
		if(!this.webcast ||
			!value
		) {
			return Promise.resolve(null);
		}
		this.webcast.shortcutName = value;

		this.shortcutEvents = null;
		return this.ShortcutService.getWebcastShortcutSchedule(this.webcast)
			.then(events => {
				this.shortcutEvents = events;
				if(events.hasConflict) {
					return Promise.resolve({ shortcutNameInUse: true });
				}
				return Promise.resolve(null);
			});
	};

	public getShortcutUrl(): string {
		return this.ShortcutService.getShortcutUrl(this.webcast);
	}

	public showShortcutSchedule(): void {
		this.Dialog.getDialog('ShortcutSchedule').open({
			initialState: {
				resource: {
					reservationDate: this.webcast.startDate,
					isSelectedResource: false,
					videoSourceName: this.webcast.shortcutName,
					videoSourceDisplayName: '',
					events: this.shortcutEvents.map(e => ({ ...e, startDate: this.ShortcutService.calculateStartDate(e) })),
					isVideoSource: false,
					eventUrl: this.getShortcutUrl()
				}
			}
		});
	}

	public onEndDateTimeChange(): void {
		this.updateDuration();
		this.validateShortcutSchedule();
	}

	public onStartDateTimeChange(): void {
		if (this.webcast.startDate instanceof Date && this.webcast.endDate instanceof Date) {
			this.webcast.endDate = new Date(this.webcast.startDate.getTime() + this.duration);
			this.validateShortcutSchedule();
		}
	}

	public get isScheduled(): boolean {
		return !this.webcast.id ||
			this.webcast.status === WebcastStatus.Scheduled;
	}

	public get minDuration(): number {
		return this.webcast.lobbyTimeMinutes ? this.webcast.lobbyTimeMinutes * MinuteMs : 1;
	}

	public get durationDefault(): number {
		return this.webcast.lobbyTimeMinutes * MinuteMs;
	}

	public getControl(controlName: string): AbstractControl {
		return this.form.control.get(controlName);
	}

	private validateExternalErrors(): void {
		if ('automatedDateInPast' in this.externalErrors) {
			toggleError(this.getControl('endDate'), 'automatedDateInPast', this.externalErrors?.automatedDateInPast);
		}

		if (ShortcutConflictErrorKey in this.externalErrors) {
			toggleError(this.getControl('endDate'), ShortcutConflictErrorKey, this.externalErrors?.[ShortcutConflictErrorKey]);
		}
	}

}
